import { FC, ReactNode, useEffect, useState } from 'react';

import { useGameConfig } from '../../config/GameConfigProvider';
import { useTotalBetAfterSubscription } from '../../hooks/useTotalBetAfterSubscription';
import { useCoreDispatch } from '../../store/CoreStore';
import { useChipSlice } from '../../store/slices/chips/selectors';
import { ChipTypes } from '../../store/slices/chips/types';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useInitializationSlice } from '../../store/slices/initialization/selectors';
import { toastActions } from '../../store/slices/toast/slice';

/**
 * This message appears every time a player opens/refreshes a table where the selected chip is higher than the minimum bet available for the table. 
 * If the default chip is equal or lower than the min bet or is free chip - we do not show the tooltip
 * The chip shown in the toast notification is the one equal to table min bet
 * The tooltip gets removed once -
    - Player manually closes it with X action
    - Player decreases the chip by selecting a chip equal or lower than the min bet
    - Player places a bet with any chip
    - Player selects free chip
 */
export const JurisdictionNL: FC = (): ReactNode => {
  const [toastShown, setToastShown] = useState(false);
  const [toastRemoved, setToastRemoved] = useState(false);

  const dispatch = useCoreDispatch();
  const tableConfig = useConfigSlice.tableConfig();
  const defaultBetAmount = tableConfig?.defaultBetAmount;
  const isCrashGame = useGameConfig().config.isCrashGame;
  const isBetPlaced = useTotalBetAfterSubscription();
  const playerConfig = useConfigSlice.playerConfig();
  const casinoConfig = useConfigSlice.casinoConfig();
  const selectedChip = useChipSlice.getSelectedChip();
  const isReadyToRender = useInitializationSlice.getIsReadyToRender();
  const isFreeChipSelected = selectedChip?.type === ChipTypes.FreeChip;

  useEffect(() => {
    if (toastRemoved) return;
    if (!isReadyToRender) return;
    if (playerConfig?.jurisdiction !== 'NL') return;
    if (!casinoConfig?.showNlMinBetToast) return;
    const tableLimits = tableConfig?.tableBetLimit;
    if (!tableLimits) return;
    const betingAmountValue = isCrashGame ? (defaultBetAmount ?? 0) : selectedChip.value;
    if (!betingAmountValue) return;

    if (!toastShown || !isFreeChipSelected) {
      if (betingAmountValue > tableLimits.min) {
        setToastShown(true);
        dispatch(toastActions.createToast('TOAST_BET_ABOVE_MINIMUM'));
      }
    }

    if (toastShown) {
      if (betingAmountValue <= tableLimits.min || isBetPlaced || isFreeChipSelected) {
        setToastRemoved(true);
        dispatch(toastActions.removeNLToast());
      }
    }
  }, [
    casinoConfig,
    defaultBetAmount,
    dispatch,
    isBetPlaced,
    isCrashGame,
    isFreeChipSelected,
    isReadyToRender,
    playerConfig,
    selectedChip,
    tableConfig,
    toastRemoved,
    toastShown,
  ]);

  return null;
};
